import { inject, Pipe, PipeTransform } from '@angular/core';

import { GraphService } from '@webgate/angular-azure-core';
import { forkJoin, map, Observable, of } from 'rxjs';

import { UserService } from '../services/user.service';

@Pipe({
  name: 'userName',
  pure: true
})
export class UserNamePipe implements PipeTransform {
  private graphService = inject(GraphService);
  private userService = inject(UserService);

  /**
   * Resolves user display name(s) from Azure IDs with support for stored-name fallback.
   *
   * Fallback order per user:
   * 1) Azure displayName (if user can be resolved and is not "Unknown User")
   * 2) Stored name from payload (if provided as first pipe argument)
   * 3) Original ID/input value
   *
   * Backward-compatible usage:
   * - `id | userName | async`
   * - `id | userName:'firstName' | async`
   * - `id | userName:'lastName' | async`
   *
   * Extended usage with stored-name fallback:
   * - `id | userName:storedName | async`
   * - `ids | userName:storedNames | async`
   * - `id | userName:storedName:'firstName' | async`
   *
   * @param input Azure ID(s) or legacy notes-style names.
   * @param args Optional first argument for stored name(s), followed by optional name selector(s): 'firstName'|'lastName'.
   * @returns Observable with formatted display name(s).
   */
  transform(input: string | string[] | null, ...args: unknown[]): Observable<string> {
    const { storedNames, nameArgs } = this.extractArgs(args);
    if (!input) {
      return of(this.joinNames(this.normalizeNames(storedNames)));
    }
    if (Array.isArray(input)) {
      return this.transformMultiple(input, storedNames, nameArgs);
    }
    return this.transformSingle(input, this.getSingleStoredName(storedNames), nameArgs);
  }

  private transformSingle(userAzureId: string, storedName: string, nameArgs: string[]): Observable<string> {
    if (!this.graphService.isSortOfValidAzureId(userAzureId)) {
      const fallbackName = storedName || this.extractNotesName(userAzureId);
      return of(this.getMatchingName(fallbackName, nameArgs));
    }
    return this.userService
      .getUser(userAzureId)
      .pipe(
        map((user) => {
          const resolvedName = user?.displayName ?? '';
          const hasResolvedName = !!resolvedName && !resolvedName.includes('Unknown User');
          const displayName = hasResolvedName ? resolvedName : storedName || userAzureId;
          return this.getMatchingName(displayName, nameArgs);
        })
      );
  }

  private transformMultiple(userAzureIds: string[], storedNames: string[], nameArgs: string[]): Observable<string> {
    const transformedUsers: Observable<string>[] = userAzureIds.map((userAzureId, index) =>
      this.transformSingle(userAzureId, this.getStoredNameAtIndex(storedNames, userAzureId, index), nameArgs)
    );
    return forkJoin(transformedUsers).pipe(map((results) => this.joinNames(results)));
  }

  /** Applies optional name slicing ('firstName' | 'lastName') to a display name. */
  private getMatchingName(displayName: string, args: string[]): string {
    const split = displayName.split(' ');
    if (args.includes('firstName')) return split[0] ?? '';
    else if (args.includes('lastName')) return split.splice(1).join(' ') ?? '';
    return displayName;
  }

  private extractArgs(args: unknown[]): { storedNames: string[]; nameArgs: string[] } {
    const firstArg = args[0];
    const isNameArg = (value: unknown): value is string => value === 'firstName' || value === 'lastName';

    if (isNameArg(firstArg)) {
      return {
        storedNames: [],
        nameArgs: args.filter(isNameArg)
      };
    }

    return {
      storedNames: this.normalizeNames(firstArg as string | string[] | null | undefined),
      nameArgs: args.slice(1).filter(isNameArg)
    };
  }

  private normalizeNames(value: string | string[] | null | undefined): string[] {
    if (Array.isArray(value)) {
      return value.map((entry) => (entry ?? '').trim()).filter((entry) => entry.length > 0);
    }

    const singleValue = (value ?? '').trim();
    return singleValue.length > 0 ? [singleValue] : [];
  }

  private getSingleStoredName(storedNames: string[]): string {
    return storedNames[0] ?? '';
  }

  private getStoredNameAtIndex(storedNames: string[], fallbackUserId: string, index: number): string {
    const storedName = storedNames[index];
    return storedName ? storedName : fallbackUserId;
  }

  private joinNames(values: string[]): string {
    return values.filter((value) => value.length > 0).join(', ');
  }

  /** Keeps legacy Notes/unknown input unchanged as terminal fallback value. */
  private extractNotesName(input: string): string {
    if (!input) return '';
    return input;
  }
}
